import { useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { useInvoices, useClientPayments, useInvoiceActions, isPayable, PAYMENT_METHOD_LABEL } from "@/modules/invoices/api";
import { InvoicesTable } from "@/modules/invoices/InvoicesPage";
import { dateTime, money } from "@/lib/format";
import { EmptyState } from "@/components/shared/EmptyState";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

export function ClientInvoicesTab({ clientId, currency }: { clientId: string; currency: "EUR" | "USD" }) {
  const nav = useNavigate();
  const { data: invoices } = useInvoices({ clientId });
  const { data: payments } = useClientPayments(clientId);
  const actions = useInvoiceActions();
  const due = invoices?.filter(isPayable).reduce((s, i) => s + Number(i.amount_due), 0) ?? 0;
  const overdue = invoices?.filter((i) => i.status === "overdue").length ?? 0;
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm">Restant dû <span className={`font-semibold ${due > 0 ? "text-destructive" : ""}`}>{money(due, currency)}</span>{overdue > 0 && <> · <span className="font-medium text-destructive">{overdue} échue{overdue > 1 ? "s" : ""}</span></>}</p>
        <Button size="sm" onClick={() => actions.free.mutate({ clientId }, { onSuccess: (id) => nav(`/factures/${id}/modifier`) })} disabled={actions.free.isPending}><Plus className="mr-2 h-4 w-4" />Facture libre</Button>
      </div>
      {invoices && invoices.length === 0 ? <EmptyState title="Aucune facture" description="Les factures se créent depuis une commande (acompte, solde) ou ici en facture libre." /> : invoices && <InvoicesTable invoices={invoices} onOpen={(id) => nav(`/factures/${id}`)} />}
      <section>
        <h3 className="mb-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground">Paiements reçus</h3>
        {!payments?.length ? <p className="text-sm text-muted-foreground">Aucun paiement.</p> : (
          <ul className="glass divide-y divide-border/60 rounded-2xl">
            {payments.map((p) => (
              <li key={p.id} className="flex items-center justify-between gap-3 px-4 py-2 text-sm">
                <span>{PAYMENT_METHOD_LABEL[p.method]}{p.source === "stripe" ? " · Stripe" : p.source === "mercury" ? " · Mercury" : ""}{!p.livemode && <Badge variant="destructive" className="ml-2">TEST</Badge>}{p.reference ? <span className="text-muted-foreground"> · {p.reference}</span> : ""}</span>
                <span className="text-right"><span className="font-medium">{money(p.amount, p.currency)}</span><span className="block text-xs text-muted-foreground">{dateTime(p.received_at)}</span></span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
